import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import {
  getJobsiteSelected,
  getSelectedBusinessLine,
  OrderBuilderState,
} from '@ordersnproducts/modules/order-builder/reducers';
import { Observable, of } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom, filter } from 'rxjs/operators';
import {
  LoadCalendarsAction,
  LoadCalendarsCompleteAction,
  LoadFailCalendarsAction,
  CalendarsActionTypes,
  LoadCalendarsV2Action,
} from '../../actions/calendar-actions/calendars.actions';
import * as fromExperiencesActions from '@ordersnproducts/modules/order-builder/modules/location/actions/experiences-actions/experiences.actions';
import { CalendarsService, CalendarsResponse } from '@cemex-core/cmx-api';
import { ExperiencesService } from '@ordersnproducts/modules/shared/services/experiences.service';
import { CalendarType } from '@ordersnproducts/modules/shared/models/calendar-type.enum';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';

@Injectable()
export class CalendarsEffects {
  loadCalendars$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromExperiencesActions.ActionTypes.SET_EXPERIENCE),
        switchMap(() =>
          this.rmsExperience.isRmsULExperienceEnabled$().pipe(
            map((isRmxUlEnabled) => {
              if (isRmxUlEnabled || this.rmsExperience.isArkikUlEnabled()) {
                this.store.dispatch(new LoadCalendarsV2Action());
              } else if (this.rmsExperience.isRmsEnabled() || this.rmsExperience.commandDcoEnabled()) {
                this.store.dispatch(new LoadCalendarsAction());
              } else {
                this.store.dispatch(new LoadFailCalendarsAction());
              }
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  getCalendars$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CalendarsActionTypes.LOAD_CALENDARS),
      withLatestFrom(this.store.pipe(select(getSelectedBusinessLine)), this.store.pipe(select(getJobsiteSelected))),
      filter(([action, businessLine, jobsite]) => !!businessLine && !!jobsite),
      switchMap(([action, businessLine, jobsite]) => {
        return this.calendarsService.getCalendars(jobsite.shipmentLocationCode, businessLine.productTypeCode).pipe(
          map((response: CalendarsResponse) => {
            if (this.validateCalendar(response)) {
              const res = [...response.calendars];
              res[0].subGroup.subGroupCode = isNaN(Number(res[0].subGroup.subGroupCode))
                ? res[0].subGroup.subGroupCode
                : res[0].subGroup.subGroupCode.padStart(4, '0');
              return new LoadCalendarsCompleteAction(res);
            }

            return new LoadCalendarsCompleteAction(response.calendars);
          }),
          catchError((error) => {
            this.store.dispatch(new fromExperiencesActions.SetDefaultExperience());
            return of(new LoadFailCalendarsAction());
          }),
        );
      }),
    ),
  );

  /**
   * Effect to upload calendars (v2) based on the line of business and workplace selected for the RMXUL experience.
   *
   * Listens for the `LOAD_CALENDARS_V2` action, retrieves calendars from the service,
   * and dispatches a success or failure action. Formats subgroup codes if valid data is returned.
   * Dispatches a default experience action on validation failure or errors.
   *
   * @effect
   * @returns {Observable<Action>} Emits actions for successful or failed calendar loading.
   */

  getCalendarsV2$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CalendarsActionTypes.LOAD_CALENDARS_V2),
      withLatestFrom(this.store.pipe(select(getSelectedBusinessLine)), this.store.pipe(select(getJobsiteSelected))),
      filter(([action, businessLine, jobsite]) => !!businessLine && !!jobsite),
      switchMap(([action, businessLine, jobsite]) => {
        return this.calendarsService.getCalendarsV2(jobsite.shipmentLocationCode, businessLine.productTypeCode).pipe(
          map((response: CalendarsResponse) => {
            if (this.validateCalendar(response)) {
              const res = [...response.calendars];
              res[0].subGroup.subGroupCode = isNaN(Number(res[0].subGroup.subGroupCode))
                ? res[0].subGroup.subGroupCode
                : res[0].subGroup.subGroupCode.padStart(4, '0');
              return new LoadCalendarsCompleteAction(res);
            }

            return new LoadCalendarsCompleteAction(response.calendars);
          }),
          catchError((error) => {
            this.store.dispatch(new fromExperiencesActions.SetDefaultExperience());
            return of(new LoadFailCalendarsAction());
          }),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private calendarsService: CalendarsService,
    private rmsExperience: ExperiencesService,
  ) {}

  validateCalendar(response: CalendarsResponse): boolean {
    const commandDco = this.rmsExperience.commandDcoEnabled();
    const firstCalendar = _safe(() => response.calendars[0]);
    const calendarType = commandDco ? CalendarType.MIXTO : _safe(() => firstCalendar.calendarType);
    const offerDays = _safe(() => firstCalendar.offerDays, 0);
    const validCalendar =
      (!!firstCalendar && !!calendarType && offerDays > 0) || this.rmsExperience.commandDcoEnabled();
    return validCalendar && Object.values(CalendarType).includes(calendarType as CalendarType);
  }
}
